"use client";

import { Icon } from "@ospweb/sdk-ui";
import { useState } from "react";

import { useIsPc } from "@/src/hooks";

import { cn } from "utils";
import { useMemoizedFn } from "ahooks";
import { Popover } from "@/src/components/Popover";
import type { ChatTabMoreMenuItemUiStateMap } from "@/src/utils/chatTabMoreMenuItemUiState";
import Translations from "@translate/components/ChatTabComp/ChatTabMoreMenu";
import { I18nUtil } from "@utils/i18n";

const CHAT_TAB_MORE_MENU_ITEM_IDS = [
  "bookmark",
  "share",
  "rename",
  "history",
  "new-chat",
] as const;

export type ChatTabMoreMenuItemId =
  (typeof CHAT_TAB_MORE_MENU_ITEM_IDS)[number];

const I18n = I18nUtil.createScoped(Translations);

export type ChatTabMoreMenuProps = {
  className?: string;
  /** 每项展示态；未传的项视为 visible、非 disabled，图标用默认 meta */
  itemUi?: ChatTabMoreMenuItemUiStateMap;
  onItemClick?: (itemId: ChatTabMoreMenuItemId) => void;
  onOpenChange?: (open: boolean) => void;
};

export function ChatTabMoreMenu({
  className,
  itemUi,
  onItemClick,
  onOpenChange,
}: ChatTabMoreMenuProps) {
  const [isPc] = useIsPc();
  // 这里定义在组件内部：避免“仅类型存在/编译期推导”与“运行时真实 key 集合”出现漂移
  const menuItemMetas = [
    { id: "bookmark", labelKey: I18n.t("Bookmark"), icon: "star" },
    { id: "share", labelKey: I18n.t("Share"), icon: "expand-alt" },
    { id: "rename", labelKey: I18n.t("Rename"), icon: "edit-alt" },
    { id: "history", labelKey: I18n.t("History"), icon: "time-five" },
    { id: "new-chat", labelKey: I18n.t("New Chat"), icon: "plusback-to-topxz" },
  ] as const satisfies ReadonlyArray<{
    id: ChatTabMoreMenuItemId;
    labelKey: string;
    icon: string;
  }>;

  const [open, setOpen] = useState(false);

  const handleOpenChange = useMemoizedFn((next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  });

  const handleItemClick = useMemoizedFn((id: ChatTabMoreMenuItemId) => {
    setOpen(false);
    onOpenChange?.(false);
    onItemClick?.(id);
  });

  const renderMenuItems = useMemoizedFn(
    (group: readonly ChatTabMoreMenuItemId[]) =>
      group.flatMap((id) => {
        const item = menuItemMetas.find((m) => m.id === id);
        if (!item) {
          return [];
        }

        const ui = itemUi?.[item.id];
        if (ui?.visible === false) {
          return [];
        }

        const disabled = ui?.disabled === true;
        const icon = ui?.icon ?? item.icon;
        const iconClassName = cn(
          "shrink-0",
          ui?.iconClassName ?? "text-content_primary",
        );

        return [
          <button
            key={item.id}
            type="button"
            role="menuitem"
            disabled={disabled}
            aria-disabled={disabled}
            className={cn(
              "flex h-10 w-full items-center gap-2 px-2 text-left",
              "body_medium_m text-content_primary",
              "transition-colors hover:bg-background_white_overlay5",
              disabled && "cursor-not-allowed opacity-60",
            )}
            onClick={() => {
              if (disabled) {
                return;
              }
              handleItemClick(item.id);
            }}
          >
            <Icon name={icon} size={16} className={iconClassName} aria-hidden />
            <span className={cn(disabled && "text-content_tertiary")}>
              {item.labelKey}
            </span>
          </button>,
        ];
      }),
  );

  // PC 端 bookmark/share 渲染为外部行内按钮
  const pcInlineIds = [
    "bookmark",
    "share",
  ] as const satisfies readonly ChatTabMoreMenuItemId[];
  const groupA = isPc
    ? (["rename"] as const)
    : (["bookmark", "share", "rename"] as const);
  const groupB = ["history", "new-chat"] as const;
  const renderedGroupA = renderMenuItems(groupA);
  const renderedGroupB = renderMenuItems(groupB);

  const showDivider = renderedGroupA.length > 0 && renderedGroupB.length > 0;

  // PC 端：bookmark/share 渲染为 dropdown 外的行内按钮
  const pcInlineButtons = isPc
    ? pcInlineIds.flatMap((id) => {
        const item = menuItemMetas.find((m) => m.id === id);
        if (!item) {
          return [];
        }
        const ui = itemUi?.[item.id];
        if (ui?.visible === false) {
          return [];
        }
        const disabled = ui?.disabled === true;
        const icon = ui?.icon ?? item.icon;
        return [
          <button
            key={item.id}
            type="button"
            disabled={disabled}
            aria-disabled={disabled}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-2 rounded-1",
              "button_m text-content_primary",
              "transition-colors hover:bg-background_white_overlay5",
              disabled && "cursor-not-allowed opacity-60",
            )}
            onClick={() => {
              if (disabled) {
                return;
              }
              handleItemClick(item.id);
            }}
          >
            <Icon
              name={icon}
              size={16}
              className={cn(
                "shrink-0",
                ui?.iconClassName ?? "text-content_primary",
              )}
              aria-hidden
            />
            <span className={cn(disabled && "text-content_tertiary")}>
              {item.labelKey}
            </span>
          </button>,
        ];
      })
    : [];

  const menuContent = (
    <div
      className={cn(
        "flex w-40 flex-col overflow-hidden rounded-2 border border-border_white_transparent10",
        "bg-background_solid_neutral_primary p-2",
        "shadow-[3px_3px_16px_rgba(0,0,0,0.08)]",
      )}
      role="menu"
    >
      {renderedGroupA}
      {showDivider ? (
        <div
          role="separator"
          aria-orientation="horizontal"
          className="my-1 border-t border-border_white_transparent10"
        />
      ) : null}
      {renderedGroupB}
    </div>
  );

  return (
    <div className={cn("inline-flex items-center gap-3", className)}>
      <div className="flex gap-2 items-center">{pcInlineButtons}</div>
      <Popover
        open={open}
        onVisibilityChange={handleOpenChange}
        trigger="click"
        placement="bottom-end"
        contentOffset={{ bottom: 16, left: -16 }}
        contentAnimation="spring"
        content={menuContent}
        contentClassName="border-0 bg-transparent p-0 shadow-none"
        className="inline-flex"
      >
        <button
          type="button"
          className="inline-flex size-6 items-center justify-center"
          aria-label="More"
          aria-expanded={open}
          aria-haspopup="menu"
        >
          <Icon
            name="dots-horizontal-rounded"
            size={24}
            className="text-content_primary"
            aria-hidden
          />
        </button>
      </Popover>
    </div>
  );
}
